import i18n from '@/plugins/i18n'

import { useLookup } from '@/composables/data-grid'
import { useNotify } from '@/composables/notify'
import { usePriceColumn, useQuantityColumn, useNotesColumn, useSupplierBrandColumn, useTotalNoVatColumn, useTotalVatColumn, useVatTypeColumns } from '@/composables/data-grid-column'

import { usePortalStore } from '@/stores/portal.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { menuRoutesEnum } from '@/router'

const EPC = '/epc/'
const AUTODATA = '/autodata/'
const catalog_filters = {
  epc: {
    vin: 'telaio_filter',
  },
  autodata: {
    vin: 'telaio_filter',
    plate: 'targa_filter',
    infobike: 'infobike_filter',
  }
}

export const useCatalog = () => {
  const { t } = i18n.global
  const portalStore = usePortalStore()
  const registryStore = useRegistryStore()
  const webSiteStore = useWebSiteStore()
  const { notifyError } = useNotify()

  const catalogWindows = new Map()
  let isOpeningAutodata = false

  const openCatalogWindow = (url, e, target = 'catalogTabTarget', ratio = 0.8) => {
    const current = catalogWindows.get(target)
    if (!current || current.tab.closed) {
      let tab
      if (e.targetBlank) {
        tab = window.open(url, '_blank')
      } else {
        const height = window.innerHeight * ratio
        const width = window.innerWidth * ratio
        const left = window.screenX + (window.innerWidth - width) / 2
        const top = window.screenY + (window.innerHeight - height) / 2
        tab = window.open(url, target, `left=${left}, top=${top}, width=${width}, height=${height}, status=no, toolbar=no, location=no, menubar=no, resizable=1`)
      }
      if (tab) catalogWindows.set(target, { tab, url })
      return tab
    }
    if (url !== current.url) {
      current.tab.location.href = url
      current.url = url
    }
    current.tab.focus()
    return current.tab
  }

  const useColumns = () => {
    return [
      useSupplierBrandColumn({
        dataField: 'supplier_brand_code',
        lookup: useLookup({ store: registryStore.supplierBrands, valueExpr: 'code' }),
        width: '5rem'
      }),
      { dataField: 'code', caption: t('common.code'), sortOrder: 'desc', width: '8rem' },
      { dataField: 'name', caption: t('common.description') },
      useNotesColumn({ visible: true }),
      useQuantityColumn(),
      usePriceColumn({ caption: t('common.priceNoVat') }),
      ...useVatTypeColumns(),
      useTotalNoVatColumn(),
      useTotalVatColumn(),
    ]
  }

  const getCatalogItems = () => {
    return portalStore.getLocalizedCatalogs().filter(i => i.code !== 'lrw' || (i.code === 'lrw' && webSiteStore.user.has_catalog_lrw_catalog)).map(i => ({
      id: i.id,
      source: i.code === 'lrw' ? webSiteStore.importRowTypesEnum.LRW : webSiteStore.importRowTypesEnum.CATALOG,
      text: i.name,
    }))
  }

  const getAutodataItems = () => {
    if (!webSiteStore.hasClaim(webSiteStore.claimsEnum.AUTODATA_READ)) return []

    return [{
      source: webSiteStore.importRowTypesEnum.AUTODATA,
      text: t('common.importRowType.autodata'),
    }]
  }

  const getAvailableCatalogItems = (documentType, displayedSources, { includeAutodata = false } = {}) => {
    const isAutodataTarget = [
      portalStore.documentTypesEnum.GARAGE_QUOTE,
      portalStore.documentTypesEnum.GARAGE_WORKSHEET,
    ].includes(documentType)

    const items = [
      ...getCatalogItems(),
      ...(isAutodataTarget || includeAutodata ? getAutodataItems() : []),
    ]

    return displayedSources?.length ? items.filter(item => displayedSources.includes(item.source)) : items
  }

  const onCatalogLinkClick = (e) => {
    const defaultCatalogUrl = portalStore.catalogs.find(i => i.is_default)?.url
    let catalogUrl = defaultCatalogUrl
    if (e.brandCode && webSiteStore.hasClaim(`epc.${e.brandCode}.read`)) {
      const url = new URL(`${EPC}${encodeURIComponent(e.brandCode)}`, defaultCatalogUrl)
      if (e.vin) url.searchParams.set(catalog_filters.epc.vin, e.vin)
      catalogUrl = url.href
    }

    return openCatalogWindow(catalogUrl, e)
  }

  const onAutodataLinkClick = async(e) => {
    if (isOpeningAutodata || !webSiteStore.hasClaim(webSiteStore.claimsEnum.AUTODATA_READ)) return

    const url = new URL(`${AUTODATA}`, portalStore.catalogs.find(item => item.is_default)?.url)
    const filter = [
      [catalog_filters.autodata.infobike, e.vehicle?.infoBikeCodeFull],
      [catalog_filters.autodata.plate, e.vehicle?.plate_number],
      [catalog_filters.autodata.vin, e.vehicle?.vin],
    ].map(([key, value]) => [key, value?.toString().trim()]).find(([, value]) => value)

    if (!filter || filter[0] === catalog_filters.autodata.infobike) {
      if (filter) url.searchParams.set(...filter)
      return openCatalogWindow(url.href, e, 'autodataTabTarget')
    }

    const popup = openCatalogWindow('about:blank', e, 'autodataTabTarget')
    if (!popup) return

    isOpeningAutodata = true
    try {
      url.searchParams.set(...filter)
      return openCatalogWindow(url.href, e, 'autodataTabTarget')
    } catch {
      popup.close()
      notifyError(t('error.credits_service_unavailable'))
    } finally {
      isOpeningAutodata = false
    }
  }

  const onCatalogLrwLinkClick = (e) => {
    let defaultCatalogUrl = portalStore.catalogs.find(i => i.code === 'lrw')?.url
    if (!webSiteStore.user.has_catalog_lrw_catalog) {
      localStorage.setItem('allow_lrw_denied_page', 'true')
      const localizations = {
        'common.requestInfo': i18n.global.t('common.requestInfo'),
        'common.contactName': i18n.global.t('common.contactName'),
        'common.phone': i18n.global.t('common.phone'),
        'common.email': i18n.global.t('common.email'),
        'common.note': i18n.global.t('common.note', 2),
        'common.submit': i18n.global.t('common.submit'),
        'common.catalogInfoRequest': i18n.global.t('common.catalogInfoRequest'),
        'common.customerCode': i18n.global.t('common.customerCode'),
        'common.nameAlt': i18n.global.t('common.nameAlt'),
        'common.emailSent': i18n.global.t('common.emailSent'),
        'error.sendingRequest': i18n.global.t('error.sendingRequest'),
        'common.accessRestrictedCatalog': i18n.global.t('common.accessRestrictedCatalog'),
      }
      localStorage.setItem('catalog_lrw_translation', JSON.stringify(localizations))
      defaultCatalogUrl = menuRoutesEnum.CATALOG_FORM
    } else {
      const infobike = e.vehicle?.infoBikeCodeFull?.trim().slice(0, -2)
      if (infobike) {
        const url = new URL(defaultCatalogUrl)
        url.searchParams.set('infocar', infobike)
        defaultCatalogUrl = url.href
      }
    }

    return openCatalogWindow(defaultCatalogUrl, e, 'catalogTabTarget', 0.6)
  }

  return { getCatalogItems, getAutodataItems, getAvailableCatalogItems, useColumns, onCatalogLinkClick, onCatalogLrwLinkClick, onAutodataLinkClick }
}
